import React from "react";
import styled from "styled-components";

const Page = styled.div`
  min-height: 100vh;
  padding: 118px 20px 80px;
  background:
    radial-gradient(860px at 12% 0%, rgba(245, 158, 11, 0.12), transparent 58%),
    radial-gradient(900px at 88% 6%, rgba(56, 189, 248, 0.12), transparent 60%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
`;

const Wrap = styled.div`
  max-width: 920px;
  margin: 0 auto;
`;

const Shell = styled.div`
  padding: 30px;
  border-radius: 30px;
  background: linear-gradient(180deg, rgba(8, 16, 29, 0.96), rgba(5, 11, 22, 0.92));
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.24);
`;

const Eyebrow = styled.div`
  display: inline-flex;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.16);
  color: #fbbf24;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 18px 0 10px;
  color: #f8fafc;
  font-size: clamp(2rem, 4vw, 3.3rem);
  line-height: 1.06;
`;

const Lead = styled.p`
  margin: 0;
  color: #94a3b8;
  font-size: 15px;
  line-height: 1.8;
`;

const Section = styled.section`
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid rgba(148, 163, 184, 0.12);
`;

const SectionTitle = styled.h2`
  margin: 0 0 12px;
  color: #f8fafc;
  font-size: 1.3rem;
`;

const Text = styled.p`
  margin: 0;
  color: #cbd5e1;
  font-size: 14px;
  line-height: 1.85;
`;

const Table = styled.div`
  margin-top: 14px;
  display: grid;
  gap: 12px;
`;

const Row = styled.div`
  padding: 16px;
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.56);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const Label = styled.div`
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Value = styled.div`
  margin-top: 8px;
  color: #f8fafc;
  font-size: 15px;
  font-weight: 800;
`;

const Desc = styled.p`
  margin: 8px 0 0;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.75;
`;

export default function CookiesPolicyPage() {
  return (
    <Page>
      <Wrap>
        <Shell>
          <Eyebrow>Cookies Policy</Eyebrow>
          <Title>Cookies and browser storage at skilll.dev</Title>
          <Lead>
            Last updated: April 5, 2026. This page explains what cookies or similar
            browser storage technologies are currently used by skilll.dev and why.
          </Lead>

          <Section>
            <SectionTitle>What we currently use</SectionTitle>
            <Text>
              Based on the current application setup, skilll.dev primarily uses
              browser storage for functional purposes rather than advertising or
              retargeting. Some functionality may rely on local storage or related
              browser persistence through app and authentication tooling.
            </Text>

            <Table>
              <Row>
                <Label>Functional storage</Label>
                <Value>Authentication-related client persistence</Value>
                <Desc>
                  Used to help maintain signed-in app behavior and related client
                  auth state where supported by the app and authentication provider.
                </Desc>
              </Row>

              <Row>
                <Label>Preference storage</Label>
                <Value>Theme preference</Value>
                <Desc>
                  Used to remember a user&apos;s selected theme or visual preference
                  across visits.
                </Desc>
              </Row>
            </Table>
          </Section>

          <Section>
            <SectionTitle>What we do not currently rely on</SectionTitle>
            <Text>
              We do not currently describe the main app as using advertising,
              retargeting, or marketing cookies based on the present codebase review.
              If analytics, pixels, ad technology, or non-essential personalization
              tools are added later, this page should be updated before launch.
            </Text>
          </Section>

          <Section>
            <SectionTitle>Managing browser storage</SectionTitle>
            <Text>
              You can clear cookies and browser storage from your browser settings.
              Please note that doing so may sign you out, remove saved preferences,
              or affect normal app functionality.
            </Text>
          </Section>

          <Section>
            <SectionTitle>Updates</SectionTitle>
            <Text>
              This policy should be reviewed again before launch, especially if you
              add analytics, customer support chat, ad pixels, or any new third-party
              tracking scripts.
            </Text>
          </Section>
        </Shell>
      </Wrap>
    </Page>
  );
}
